import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/Button';
import { ProgressBar } from '../components/ui/ProgressBar';
import { Skeleton } from '../components/ui/Skeleton';
import { WaveformEditor } from '../components/waveform/WaveformEditor';
import {
  confirmCut,
  countConfirmed,
  cutsFromApi,
  cutsToApi,
  nextCutId,
  nudgeCut,
  type EditorCut,
} from '../components/waveform/model';
import { useJobPoll } from '../hooks/useJobPoll';
import { jobs } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { track } from '../lib/analytics';
import { useToast } from '../components/ui/Toast';
import { IconArrowRight } from '../components/ui/icons';

type Step = 'waiting' | 'editing' | 'rendering' | 'rendered' | 'failed';

export function JobReview() {
  const { jobId = '' } = useParams();
  const { t, fmt } = useI18n();
  const toast = useToast();

  const { job, error, loading } = useJobPoll(jobId);
  const [cuts, setCuts] = useState<EditorCut[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [childJobId, setChildJobId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [live, setLive] = useState('');

  const childPoll = useJobPoll(childJobId);

  // Hydrate the editor model once, when the parent job first exposes cut points.
  useEffect(() => {
    if (!hydrated && job && job.cut_points.length > 0) {
      setCuts(cutsFromApi(job.cut_points));
      setHydrated(true);
    }
  }, [job, hydrated]);

  const durationMs = useMemo(() => {
    const fromCuts = cuts.reduce((m, c) => Math.max(m, c.timestampMs), 0);
    // Pad past the last cut so the final segment is visible.
    return Math.max(30000, Math.round(fromCuts * 1.15));
  }, [cuts]);

  const step: Step = useMemo(() => {
    if (childPoll.job?.state === 'complete') return 'rendered';
    if (childJobId) return 'rendering';
    if (job?.state === 'failed' || job?.state === 'dlq') return 'failed';
    if (hydrated || job?.state === 'awaiting_user_input') return 'editing';
    return 'waiting';
  }, [job, hydrated, childJobId, childPoll.job]);

  const confirmedCount = countConfirmed(cuts);
  const selectedCut = cuts.find((c) => c.id === selectedId) ?? null;

  const onNudge = useCallback(
    (id: string, deltaMs: number) => setCuts((list) => list.map((c) => (c.id === id ? nudgeCut(c, deltaMs, durationMs) : c))),
    [durationMs],
  );
  const onConfirmCut = useCallback((id: string) => setCuts((list) => list.map((c) => (c.id === id ? confirmCut(c) : c))), []);
  const onDelete = useCallback(
    (id: string) => {
      setCuts((list) => list.filter((c) => c.id !== id));
      setSelectedId((cur) => (cur === id ? null : cur));
    },
    [],
  );
  const onAdd = useCallback((atMs: number) => {
    const cut: EditorCut = { id: nextCutId(), timestampMs: Math.round(atMs), confidence: null, status: 'adjusted' };
    setCuts((list) => [...list, cut]);
    setSelectedId(cut.id);
  }, []);

  const submitConfirm = useCallback(async () => {
    if (!job || confirmedCount === 0) return;
    setSubmitting(true);
    try {
      await jobs.setCutPoints(job.id, cutsToApi(cuts));
      const res = await jobs.confirm(job.id);
      setChildJobId(res.child_job_id);
      // Funnel step: the cuts are in and the render is dispatched.
      track('cuts_confirmed', { job_id: job.id, cuts: confirmedCount });
      toast.push(t.review.rendering, 'info');
    } catch (err) {
      toast.push(apiErrorMessage(err, t), 'error');
    } finally {
      setSubmitting(false);
    }
  }, [job, cuts, confirmedCount, t, toast]);

  return (
    <div>
      <PageHeader title={t.review.title} subtitle={t.review.subtitle} />

      <p aria-live="polite" className="sr-only">
        {live}
      </p>

      {step === 'waiting' ? (
        <div className="rounded border border-info bg-surface-2 p-8 text-center" aria-live="polite">
          <div className="mx-auto max-w-sm">
            <h2 className="type-heading-1 text-primary">{t.review.waitingTitle}</h2>
            <p className="type-body text-secondary mt-2">{t.review.waitingBody}</p>
            <div className="mt-6">
              <ProgressBar value={loading ? 15 : job?.state === 'running' ? 65 : 35} label={t.states.processing} />
            </div>
            <p className="type-caption text-muted tnum mt-3">
              {job ? `${t.states.processing} · ${job.state}` : t.common.loading}
            </p>
            {!loading ? <Skeleton className="mt-6 h-40 w-full" /> : null}
          </div>
        </div>
      ) : null}

      {step === 'failed' ? (
        <div className="rounded border border-danger bg-surface-2 p-8 text-center">
          <h2 className="type-heading-1 text-primary">{t.review.failedTitle}</h2>
          <p className="type-body text-secondary mt-2">{job?.error_message ?? t.review.failedBody}</p>
          <div className="mt-6">
            <Link to="/studio">
              <Button variant="secondary" size="lg">
                {t.upload.title}
              </Button>
            </Link>
          </div>
        </div>
      ) : null}

      {step === 'rendering' ? (
        <div className="rounded border border-info bg-surface-2 p-8 text-center" aria-live="polite">
          <h2 className="type-heading-1 text-primary">{t.review.rendering}</h2>
          <p className="type-caption text-muted tnum mt-3">{childPoll.job?.state ?? t.states.processing}</p>
          <div className="mx-auto mt-6 max-w-sm">
            <ProgressBar value={childPoll.job?.state === 'running' ? 70 : 30} label={t.review.rendering} />
          </div>
        </div>
      ) : null}

      {step === 'rendered' ? (
        <div className="rounded border border-success bg-surface-2 p-8 text-center">
          <h2 className="type-heading-1 text-primary">{t.review.renderedTitle}</h2>
          <p className="type-body text-secondary mt-2">{t.review.renderedBody}</p>
          <div className="mt-6">
            <Link to="/library">
              <Button size="lg" iconRight={<IconArrowRight size={18} />}>
                {t.review.goToLibrary}
              </Button>
            </Link>
          </div>
        </div>
      ) : null}

      {step === 'editing' ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="type-data text-primary tnum">{t.review.countLabel(cuts.length, confirmedCount)}</span>
            <details className="group">
              <summary className="type-label text-secondary hover:text-primary focus-ring inline-flex cursor-pointer list-none items-center gap-1 rounded-sm">
                ? {t.review.keysTitle}
              </summary>
              <div className="mt-2 rounded border border-border-strong bg-surface-2 p-4">
                <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
                  {t.review.keys.map(([key, desc]) => (
                    <div key={key} className="flex items-center justify-between gap-3">
                      <dt className="type-caption text-primary">
                        <kbd className="rounded-sm border border-border-strong bg-surface-3 px-1.5 py-0.5">{key}</kbd>
                      </dt>
                      <dd className="type-body-sm text-secondary flex-1 text-right">{desc}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </details>
          </div>

          <WaveformEditor
            durationMs={durationMs}
            seed={job?.id ?? jobId}
            cuts={cuts}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onNudge={onNudge}
            onConfirm={onConfirmCut}
            onDelete={onDelete}
            onAdd={onAdd}
            announce={setLive}
          />

          {/* Selected-marker toolbar (§5.5) */}
          {selectedCut ? (
            <div className="flex flex-wrap items-center gap-2 rounded border border-border-strong bg-surface-2 p-2">
              <span className="type-data text-primary tnum px-2">{fmt.durationMs(selectedCut.timestampMs)}</span>
              <div className="flex flex-wrap gap-2">
                <Button variant="ghost" size="sm" onClick={() => onNudge(selectedCut.id, -10)}>
                  {t.review.toolbar.nudgeBack}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onNudge(selectedCut.id, 10)}>
                  {t.review.toolbar.nudgeFwd}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => onConfirmCut(selectedCut.id)}>
                  {t.review.toolbar.confirmOne}
                </Button>
                <Button variant="destructive" size="sm" onClick={() => onDelete(selectedCut.id)}>
                  {t.review.toolbar.deleteOne}
                </Button>
              </div>
            </div>
          ) : null}

          <div className="sticky bottom-16 z-20 lg:bottom-0">
            <Button
              size="lg"
              block
              loading={submitting}
              disabled={confirmedCount === 0}
              onClick={submitConfirm}
            >
              {t.review.confirmCta(confirmedCount, cuts.length)}
            </Button>
          </div>

          {error && !error.isRateLimited ? (
            <p className="type-body-sm text-danger" role="alert">
              {apiErrorMessage(error, t)}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
